import { type ReactElement } from 'react';
import { Link, redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { type Quote } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { loadAwardedQuotes } from '../features/jobs/work-query.js';
import { loadSession } from '../shared/session.js';

/**
 * `W4-T09` (E8) — the jobs a professional has won, and the door to Start work on each.
 *
 * Built by `agent-jobs` on `MEM-2026-09-20-31`'s rule: an authenticated page over this slice's data.
 * A row carries the quote's price and nothing about the job's text: a provider can read a job's title
 * only while it is in their feed, which an awarded job no longer is (spec §4.4, run record).
 */
interface WorkData {
  locale: string;
  quotes: Quote[];
}

export async function loader({ params, context }: LoaderFunctionArgs): Promise<WorkData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  return { locale, quotes: await loadAwardedQuotes(context) };
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, quotes } = useLoaderData<WorkData>();

  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  return (
    <article data-testid="work">
      <h1>{t('handshake.work.title')}</h1>
      {quotes.length === 0 ? (
        <p className="mp-lead">{t('handshake.work.empty')}</p>
      ) : (
        <ul className="mp-list" data-testid="work-list">
          {quotes.map((quote) => (
            <li key={quote.id} className="mp-section">
              <Link to={`/${locale}/work/${quote.jobId}`}>
                {t('handshake.work.row', { amount: money(quote.amountCents) })}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
